import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { AlertOctagon } from 'lucide-react'
import type { ReactNode } from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { isApiError } from '@/lib/http/errors'
import { Button } from '@/shared/ui/Button'

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation('errors')
  const correlationId = isApiError(error) ? error.correlationId : undefined

  return (
    <div role="alert" className="mx-auto flex max-w-lg flex-col items-center gap-2 p-4 text-center">
      <AlertOctagon aria-hidden="true" className="size-6 text-error-600" />
      <h1 className="text-h2">{t('boundary.title')}</h1>
      <p className="text-body text-text-muted">{t('boundary.body')}</p>

      {/* A citizen phoning support needs something the agent can search on. The
          spec issues no server-side request id, so we surface the one we sent. */}
      {correlationId && (
        <p className="text-micro text-text-muted">{t('reference', { correlationId })}</p>
      )}

      <div className="mt-1 flex gap-1.5">
        <Button onClick={resetErrorBoundary}>{t('boundary.retry')}</Button>
        <Button variant="secondary" onClick={() => (window.location.href = '/')}>
          {t('boundary.home')}
        </Button>
      </div>
    </div>
  )
}

/**
 * The application's only error boundary.
 *
 * ⚠ This docstring used to describe route- and widget-level boundaries sitting inside
 * it, "notably around the camera and the signature pad". **Those do not exist.** A
 * throw in `useCamera` or `SignaturePad` — the two places that touch the most fragile
 * browser APIs in the app — unmounts the whole wizard to this fallback, losing an
 * application that no API can read back (ADR 0008).
 *
 * Adding them is worth doing and is not a like-for-like change: each needs its own
 * fallback and its own recovery path, which is UI rather than plumbing. Left as a
 * known gap rather than a silent one.
 *
 * Note also that React boundaries do not catch async rejections, and this app has
 * several fire-and-forget `void promise` call sites. There is no `unhandledrejection`
 * or `window.onerror` backstop.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const { reset } = useQueryErrorResetBoundary()

  return (
    <ErrorBoundary FallbackComponent={Fallback} onReset={reset}>
      {children}
    </ErrorBoundary>
  )
}
